import React, { useEffect, useRef } from 'react';
import {
  Building2, Search, Network, FileText, Upload, Link2, ExternalLink, Wrench,
  Layers, Globe, MessageSquare, Sparkles, Quote, MessagesSquare, BarChart3, Compass,
  Check, Clock,
} from 'lucide-react';
import {
  SEO_GEO_INTRO, SEO_GEO_FEATURES, SEO_GEO_PLANS, SEO_GEO_COMPARISON,
  SEO_GEO_STEPS, SEO_GEO_EXPLAIN, SEO_GEO_EXPECTATIONS, SEO_GEO_QUALITY,
} from '../data/seoGeoData';
import { trackEvent } from '../lib/track';
import BundleBuilder from './BundleBuilder';

const ICONS = {
  Building2, Search, Network, FileText, Upload, Link2, ExternalLink, Wrench,
  Layers, Globe, MessageSquare, Sparkles, Quote, MessagesSquare, BarChart3, Compass,
};

const scrollToEnquiry = () => {
  document.querySelector('#enquiry')?.scrollIntoView({ behavior: 'smooth' });
};

const scrollToPlans = () => {
  document.querySelector('#seo-geo-plans')?.scrollIntoView({ behavior: 'smooth' });
};

function Transition() {
  return (
    <section className="sg-transition" data-testid="sg-transition">
      <div className="pp-container">
        <p className="sg-transition-line pp-display">
          LAUNCHING YOUR WEBSITE IS THE BEGINNING—<em>NOT THE END.</em>
        </p>
      </div>
    </section>
  );
}

function IntroSection() {
  return (
    <section className="pp-section" id="seo-geo" data-testid="sg-intro">
      <div className="pp-container">
        <div className="pp-section-head">
          <span className="pp-kicker">{SEO_GEO_INTRO.eyebrow}</span>
          <h2 className="pp-h2 pp-display" style={{ marginTop: 16 }}>
            BE FOUND ON GOOGLE—AND <em>RECOMMENDED BY AI.</em>
          </h2>
          {SEO_GEO_INTRO.copy.map((p) => (
            <p className="pp-lead" key={p.slice(0, 24)}>{p}</p>
          ))}
          <p className="sg-supporting">{SEO_GEO_INTRO.supporting}</p>
        </div>
      </div>
    </section>
  );
}

function ServiceCard() {
  return (
    <section className="pp-section" style={{ paddingTop: 0 }}>
      <div className="pp-container">
        <div className="sg-hero-card" data-testid="sg-service-card">
          <div className="sg-hero-card-top">
            <div>
              <h3 className="pp-display sg-hero-title">SEO + GEO <span>GROWTH</span></h3>
              <p className="sg-hero-desc">
                An ongoing content, authority and search-visibility programme designed to help your
                website become easier to find, understand and recommend.
              </p>
            </div>
            <div className="sg-hero-price">
              <span className="pp-card-price-from" style={{ color: '#8A8FB8' }}>Recurring monthly service</span>
              <div className="pp-card-price" style={{ color: '#FF4D94', fontSize: 36 }}>
                From £595 <small style={{ color: '#B9BDDD' }}>+ VAT per month</small>
              </div>
              <p className="pp-card-inc" style={{ color: '#B9BDDD' }}>£714 including VAT</p>
              <p className="sg-hero-meta">Initial onboarding from £395 + VAT.</p>
              <p className="sg-hero-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={13} />Typical initial commitment: six months.</p>
            </div>
          </div>
          <div className="sg-features" data-testid="sg-features">
            {SEO_GEO_FEATURES.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <div className="sg-feature" key={f.label}>
                  <span className="sg-feature-icon"><Icon size={17} /></span>
                  <span>{f.label}</span>
                </div>
              );
            })}
          </div>
          <div className="sg-hero-ctas">
            <button
              className="pp-btn pp-btn-primary"
              onClick={() => { trackEvent('seo_geo_explore_plans_click', { location: 'service_card' }); scrollToPlans(); }}
              data-testid="sg-explore-plans"
            >
              Explore the monthly plans
            </button>
            <button
              className="pp-btn sg-btn-ghost"
              onClick={() => { trackEvent('growth_consultation_click', { location: 'service_card' }); scrollToEnquiry(); }}
              data-testid="sg-consultation-top"
            >
              Request a growth consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Explain() {
  return (
    <section className="pp-section pp-section-alt" data-testid="sg-explain">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">SEO MEETS THE NEW GENERATION OF <em>SEARCH.</em></h2>
        </div>
        <div className="sg-duo">
          {SEO_GEO_EXPLAIN.panels.map((panel) => (
            <div className="sg-panel" key={panel.title}>
              <h3 className="pp-display">{panel.title}</h3>
              {panel.copy.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
            </div>
          ))}
        </div>
        <p className="sg-combined" data-testid="sg-combined-note">{SEO_GEO_EXPLAIN.combined}</p>
      </div>
    </section>
  );
}

function PlanCard({ plan }) {
  return (
    <article className={`pp-card sg-plan ${plan.recommended ? 'pp-card-popular' : ''}`} data-testid={`sg-plan-${plan.id}`}>
      {plan.recommended && <span className="pp-popular-badge">Recommended</span>}
      <h3 className="pp-card-title pp-display">{plan.title}</h3>
      <div className="pp-card-price">{plan.priceEx} <small>+ VAT per month</small></div>
      <p className="pp-card-inc">{plan.priceInc} including VAT</p>
      <p className="sg-plan-setup">Setup: <strong>{plan.setupEx} + VAT</strong> · {plan.setupInc} including VAT</p>
      <p className="sg-plan-bestfor"><strong>Best for:</strong> {plan.bestFor}</p>
      <span className="sg-plan-includes-label">{plan.includesLabel}</span>
      <ul className="pp-card-list">
        {plan.includes.map((item) => (
          <li key={item}><Check size={15} /> {item}</li>
        ))}
      </ul>
      <button
        className={`pp-btn ${plan.recommended ? 'pp-btn-primary' : 'pp-btn-outline'}`}
        onClick={() => { trackEvent('seo_geo_plan_cta_click', { plan: plan.id }); scrollToEnquiry(); }}
        data-testid={`sg-cta-${plan.id}`}
      >
        {plan.cta}
      </button>
    </article>
  );
}

function Plans() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        trackEvent('seo_geo_plans_view');
        observer.disconnect();
      }
    }, { threshold: 0.2 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="pp-section" id="seo-geo-plans" data-testid="sg-plans">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">MONTHLY <em>GROWTH PLANS.</em></h2>
          <p className="pp-lead">
            Three levels of ongoing organic growth, each individually configured around your market,
            website and commercial priorities.
          </p>
        </div>
        <div className="sg-plans" ref={ref}>
          {SEO_GEO_PLANS.map((plan) => <PlanCard key={plan.id} plan={plan} />)}
        </div>
      </div>
    </section>
  );
}

function PlanComparison() {
  return (
    <section className="pp-section pp-section-alt" data-testid="sg-comparison">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">PLANS AT A <em>GLANCE.</em></h2>
        </div>
        <div className="pp-table-wrap sg-table-desktop" data-testid="sg-table">
          <table className="pp-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Monthly ex VAT</th>
                <th>Monthly inc VAT</th>
                <th>Setup ex VAT</th>
                <th>Recommended for</th>
              </tr>
            </thead>
            <tbody>
              {SEO_GEO_COMPARISON.map((row) => (
                <tr key={row.plan} className={row.recommended ? 'pp-row-popular' : ''}>
                  <td>
                    {row.plan}
                    {row.recommended && <span className="pp-tag">Recommended</span>}
                  </td>
                  <td>{row.monthlyEx}</td>
                  <td>{row.monthlyInc}</td>
                  <td>{row.setupEx}</td>
                  <td>{row.recommendedFor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sg-table-stack" data-testid="sg-table-stack">
          {SEO_GEO_COMPARISON.map((row) => (
            <div className={`sg-stack-card ${row.recommended ? 'sg-stack-recommended' : ''}`} key={row.plan}>
              <h4 className="pp-display">
                {row.plan}
                {row.recommended && <span className="pp-tag">Recommended</span>}
              </h4>
              <dl>
                <div><dt>Monthly ex VAT</dt><dd>{row.monthlyEx}</dd></div>
                <div><dt>Monthly inc VAT</dt><dd>{row.monthlyInc}</dd></div>
                <div><dt>Setup ex VAT</dt><dd>{row.setupEx}</dd></div>
                <div><dt>Recommended for</dt><dd>{row.recommendedFor}</dd></div>
              </dl>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="pp-section" data-testid="sg-how">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">HOW THE PROGRAMME <em>WORKS.</em></h2>
        </div>
        <div className="pp-process sg-steps">
          {SEO_GEO_STEPS.map((s) => (
            <div className="pp-step" key={s.step}>
              <span className="pp-step-num">{s.step}</span>
              <h4>{s.title}</h4>
              <p>{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Expectations() {
  return (
    <section className="pp-section pp-section-alt" data-testid="sg-expectations">
      <div className="pp-container">
        <div className="sg-text-duo">
          <div className="sg-text-block">
            <h2 className="pp-h2 pp-display" style={{ fontSize: 'clamp(24px, 2.6vw, 32px)' }}>
              ORGANIC GROWTH COMPOUNDS <em>OVER TIME.</em>
            </h2>
            {SEO_GEO_EXPECTATIONS.copy.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
          <div className="sg-text-block">
            <h2 className="pp-h2 pp-display" style={{ fontSize: 'clamp(24px, 2.6vw, 32px)' }}>
              AUTOMATION SUPPORTED BY <em>HUMAN JUDGEMENT.</em>
            </h2>
            {SEO_GEO_QUALITY.copy.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="sg-final" data-testid="sg-final-cta">
      <div className="pp-container">
        <h2 className="pp-display sg-final-h2">BUILD THE WEBSITE. THEN BUILD ITS VISIBILITY.</h2>
        <p className="sg-final-copy">
          Talk to Boost! about a combined website, SEO and AI-search growth programme—or add
          SEO + GEO Growth to an existing website.
        </p>
        <div className="sg-final-ctas">
          <button
            className="pp-btn sg-btn-white"
            onClick={() => { trackEvent('growth_consultation_click', { location: 'final_cta' }); scrollToEnquiry(); }}
            data-testid="sg-consultation-final"
          >
            Request a growth consultation
          </button>
          <button
            className="pp-btn sg-btn-white-outline"
            onClick={() => { trackEvent('website_plus_growth_click', { location: 'final_cta' }); scrollToEnquiry(); }}
            data-testid="sg-add-to-website"
          >
            Add SEO + GEO to my website
          </button>
        </div>
      </div>
    </section>
  );
}

export default function SeoGeoGrowth() {
  return (
    <>
      <Transition />
      <IntroSection />
      <ServiceCard />
      <Explain />
      <Plans />
      <PlanComparison />
      <BundleBuilder />
      <HowItWorks />
      <Expectations />
      <FinalCta />
    </>
  );
}
